import React, { useState, useEffect, useRef } from 'react';

// Palette luxe
const GOLD = '#d4af37';
const GOLD_LIGHT = '#f9d976';
const GOLD_DARK = '#8b6914';
const SILVER = '#c0c0c0';
const BG = '#070707';
const SURFACE = '#0e0e0e';
const SURFACE_2 = '#161616';
const BORDER = 'rgba(212, 175, 55, 0.18)';
const BORDER_STRONG = 'rgba(212, 175, 55, 0.45)';
const TEXT = '#ece6d3';
const TEXT_MUTED = '#9a9485';

// Dégradé miroir or — utilisé pour titres et boutons
const GOLD_MIRROR = `linear-gradient(110deg,
  ${GOLD_DARK} 0%,
  ${GOLD} 18%,
  ${GOLD_LIGHT} 38%,
  #ffffff 50%,
  ${GOLD_LIGHT} 62%,
  ${GOLD} 82%,
  ${GOLD_DARK} 100%)`;

const SILVER_MIRROR = `linear-gradient(110deg,
  #5a5a5a 0%,
  ${SILVER} 25%,
  #ffffff 50%,
  ${SILVER} 75%,
  #5a5a5a 100%)`;

// CSS global injecté pour fonts, keyframes et media queries
const LUXURY_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;0,6..96,500;0,6..96,600;0,6..96,700;0,6..96,800;1,6..96,400;1,6..96,500;1,6..96,600&family=Bungee+Shade&family=Cinzel:wght@400;500;600;700;800;900&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=Italiana&family=Great+Vibes&family=Allura&family=Parisienne&family=Permanent+Marker&family=Playfair+Display+SC:wght@400;700;900&family=Staatliches&family=Inter:wght@300;400;500;600;700;800&display=swap');

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes glow-pulse {
  0%, 100% { box-shadow: 0 0 0 1px rgba(212,175,55,0.25), 0 0 28px rgba(212,175,55,0.08); }
  50%      { box-shadow: 0 0 0 1px rgba(212,175,55,0.55), 0 0 60px rgba(212,175,55,0.25); }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

.lux-root { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
.lux-serif { font-family: 'Bodoni Moda', 'Didot', 'Bodoni 72', 'Times New Roman', serif; letter-spacing: 0.2px; }
.lux-italic { font-family: 'Cormorant Garamond', 'Didot', serif; font-style: italic; letter-spacing: 0.3px; }
.lux-couture { font-family: 'Italiana', 'Bodoni Moda', serif; letter-spacing: 4px; text-transform: uppercase; font-weight: 400; }
.lux-caps { font-family: 'Playfair Display SC', 'Bodoni Moda', serif; letter-spacing: 3px; }
.lux-signature {
  font-family: 'Great Vibes', 'Allura', 'Parisienne', cursive;
  letter-spacing: 1px;
  font-weight: 400;
}
.lux-number {
  font-family: 'Cinzel', 'Bodoni Moda', serif;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: ${GOLD_LIGHT};
  text-shadow: 0 1px 0 rgba(0,0,0,0.4), 0 0 8px rgba(0,0,0,0.5);
}
.lux-number-silver {
  font-family: 'Cinzel', 'Bodoni Moda', serif;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #f0f0f0;
  text-shadow: 0 1px 0 rgba(0,0,0,0.4), 0 0 8px rgba(0,0,0,0.5);
}

.lux-mirror-text {
  background: ${GOLD_MIRROR};
  background-size: 200% auto;
  -webkit-background-clip: text;
          background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer 6s linear infinite;
}
.lux-mirror-text-silver {
  background: ${SILVER_MIRROR};
  background-size: 200% auto;
  -webkit-background-clip: text;
          background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer 8s linear infinite;
}

.lux-card {
  background: linear-gradient(180deg, ${SURFACE} 0%, ${SURFACE_2} 100%);
  border: 1px solid ${BORDER};
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: transform .35s ease, border-color .35s ease, box-shadow .35s ease;
  animation: fadeInUp .6s ease both;
}
.lux-card::before {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(212,175,55,0.08) 0%, transparent 40%, transparent 60%, rgba(212,175,55,0.05) 100%);
  pointer-events: none;
}
.lux-card:hover {
  transform: translateY(-6px);
  border-color: ${BORDER_STRONG};
  box-shadow: 0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px ${BORDER_STRONG}, 0 0 50px rgba(212,175,55,0.15);
}

.lux-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, ${BORDER_STRONG} 50%, transparent 100%);
  margin: 18px 0;
}

.lux-btn-gold {
  position: relative;
  border: none;
  border-radius: 10px;
  padding: 18px 22px;
  cursor: pointer;
  color: #1a1407;
  font-weight: 600;
  font-family: inherit;
  background: ${GOLD_MIRROR};
  background-size: 200% auto;
  animation: shimmer 5s linear infinite;
  box-shadow: 0 8px 24px rgba(212,175,55,0.25), inset 0 1px 0 rgba(255,255,255,0.5);
  transition: transform .2s ease, box-shadow .2s ease;
}
.lux-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(212,175,55,0.4), inset 0 1px 0 rgba(255,255,255,0.6); }

.lux-btn-silver {
  position: relative;
  border: none;
  border-radius: 10px;
  padding: 18px 22px;
  cursor: pointer;
  color: #111;
  font-weight: 600;
  font-family: inherit;
  background: ${SILVER_MIRROR};
  background-size: 200% auto;
  animation: shimmer 7s linear infinite;
  box-shadow: 0 8px 24px rgba(192,192,192,0.2), inset 0 1px 0 rgba(255,255,255,0.6);
  transition: transform .2s ease, box-shadow .2s ease;
}
.lux-btn-silver:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(192,192,192,0.35); }

.lux-pill {
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 2.2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all .25s ease;
  font-family: 'Italiana', 'Bodoni Moda', serif;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.30);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
}
.lux-pill:hover {
  color: ${GOLD_LIGHT};
  border-color: rgba(255, 255, 255, 0.60);
  background: rgba(255, 255, 255, 0.10);
}
.lux-pill.active {
  background: ${GOLD_MIRROR};
  background-size: 200% auto;
  animation: shimmer 5s linear infinite;
  color: #1a1407;
  border-color: transparent;
  font-weight: 600;
}

.lux-input {
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid ${BORDER};
  background: rgba(0,0,0,0.4);
  color: ${TEXT};
  font-family: 'Cormorant Garamond', 'Italiana', serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
  outline: none;
  transition: border-color .25s ease, box-shadow .25s ease;
}
.lux-input::placeholder { color: ${TEXT_MUTED}; }
.lux-input:focus { border-color: ${GOLD}; box-shadow: 0 0 0 3px rgba(212,175,55,0.15); }

.lux-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 16px;
  border: 1px solid ${BORDER_STRONG};
  border-radius: 999px;
  font-family: 'Italiana', 'Bodoni Moda', serif;
  font-size: 12px;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${GOLD_LIGHT};
  background: rgba(212,175,55,0.06);
}

.lux-modal-photo {
  width: 100%;
  display: block;
  max-height: 60vh;
  object-fit: contain;
  background: #000;
}

@media (min-width: 768px) {
  .lux-modal-photo { max-height: 85vh; }
}

.lux-modal-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  align-items: stretch;
}
@media (min-width: 980px) {
  .lux-modal-grid { grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); }
}

.lux-form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}
@media (min-width: 600px) {
  .lux-form-grid { grid-template-columns: 1fr 1fr; }
  .lux-form-grid .full { grid-column: span 2; }
}

.lux-cta-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (min-width: 600px) {
  .lux-cta-row { grid-template-columns: 1fr 1fr; }
}

/* Combo Pack + Abonnement avec + au milieu */
.lux-cta-combo {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.lux-cta-combo > button { flex: 1; }
.lux-cta-plus {
  text-align: center;
  font-family: 'Cinzel', 'Bodoni Moda', serif;
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 800;
  line-height: 1;
  color: ${GOLD_LIGHT};
  text-shadow: 0 2px 8px rgba(0,0,0,0.5);
  margin: 4px 0;
  user-select: none;
  flex-shrink: 0;
}
@media (min-width: 700px) {
  .lux-cta-combo {
    flex-direction: row;
    align-items: stretch;
    gap: 10px;
  }
  .lux-cta-plus { margin: 0; align-self: center; }
}

.lux-corner {
  position: absolute;
  width: 32px; height: 32px;
  border: 1px solid ${BORDER_STRONG};
  pointer-events: none;
}

/* === Modal contact : plein écran sur mobile === */
@media (max-width: 640px) {
  .lux-contact-wrap {
    padding: 0 !important;
    align-items: stretch !important;
  }
  .lux-contact-card {
    border-radius: 0 !important;
    border: none !important;
    width: 100% !important;
    max-width: none !important;
    min-height: 100vh !important;
    box-shadow: none !important;
    padding-top: 50px !important;
  }
}

/* === Cartes petites sur mobile : on réduit / cache certaines infos === */
@media (max-width: 640px) {
  .lux-mini-card .lux-c-pseudo { display: none; }
  .lux-mini-card .lux-c-label { display: none; }
  .lux-mini-card .lux-c-divider { display: none; }
  .lux-mini-card .lux-c-bottom { padding: 8px 10px 10px !important; }
  .lux-mini-card .lux-c-name {
    font-size: 11px !important;
    margin-bottom: 3px !important;
    letter-spacing: -0.2px !important;
    line-height: 1.1 !important;
    white-space: normal !important;
    word-break: break-word !important;
  }
  .lux-mini-card .lux-c-numbers {
    gap: 6px !important;
    align-items: baseline !important;
  }
  .lux-mini-card .lux-c-num-wrap { display: contents; }
  .lux-mini-card .lux-c-num-audience,
  .lux-mini-card .lux-c-num-price {
    font-size: 11px !important;
    line-height: 1 !important;
  }
  /* Coins ornés réduits sur les mini-cartes mobile */
  .lux-mini-card .lux-corner { width: 18px !important; height: 18px !important; }
  .lux-mini-card .lux-corner.tl { top: 6px !important; left: 6px !important; }
  .lux-mini-card .lux-corner.tr { top: 6px !important; right: 6px !important; }
  .lux-mini-card .lux-corner.bl { bottom: 6px !important; left: 6px !important; }
  .lux-mini-card .lux-corner.br { bottom: 6px !important; right: 6px !important; }
}
/* === Section présentation : photo entre titre et paragraphes (mobile), photo gauche pleine hauteur (desktop) === */
.lux-presentation {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    "label"
    "title"
    "divider"
    "photo"
    "paragraphs"
    "piliers";
  row-gap: 18px;
  column-gap: 0;
  align-items: start;
}
.lux-presentation .lux-pres-label      { grid-area: label; }
.lux-presentation .lux-pres-title      { grid-area: title; }
.lux-presentation .lux-pres-divider    { grid-area: divider; }
.lux-presentation .lux-pres-photo      { grid-area: photo; max-width: 320px; margin: 12px auto; width: 100%; }
.lux-presentation .lux-pres-paragraphs { grid-area: paragraphs; }
.lux-presentation .lux-pres-piliers    { grid-area: piliers; margin-top: 14px; }
@media (min-width: 768px) {
  .lux-presentation {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "photo label"
      "photo title"
      "photo divider"
      "photo paragraphs"
      "photo piliers";
    column-gap: clamp(40px, 5vw, 72px);
    row-gap: 18px;
    align-items: center;
  }
  .lux-presentation .lux-pres-photo {
    max-width: none;
    margin: 0;
    align-self: stretch;
  }
}
/* === EDITORIAL LAYOUT — hero gauche + 2 features droite empilées === */
.lux-edito-top {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: clamp(8px, 1.5vw, 18px);
  margin-bottom: clamp(20px, 3vw, 36px);
  /* Hauteur totale du bloc : photo portrait, hero pleine hauteur */
  height: clamp(420px, 56vw, 760px);
}
.lux-edito-hero {
  position: relative;
  grid-column: 1;
  grid-row: 1 / 3;
  background: #000;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  animation: fadeInUp .6s ease both;
  min-height: 0;
  min-width: 0;
}
.lux-edito-feature {
  position: relative;
  grid-column: 2;
  background: #000;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  animation: fadeInUp .6s ease both;
  min-height: 0;
  min-width: 0;
}

.lux-card-hover-fx { transition: transform .35s ease, border-color .35s ease, box-shadow .35s ease; }
.lux-card-hover-fx:hover {
  transform: translateY(-4px);
  border-color: ${BORDER_STRONG};
  box-shadow: 0 14px 40px rgba(0,0,0,0.5), 0 0 0 1px ${BORDER_STRONG}, 0 0 40px rgba(212,175,55,0.12);
}

/* Carousel infini */
.lux-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 4px 0;
  margin-top: clamp(8px, 1.4vw, 16px);
}
.lux-carousel::before,
.lux-carousel::after {
  content: '';
  position: absolute; top: 0; bottom: 0;
  width: 60px; z-index: 2; pointer-events: none;
}
.lux-carousel::before {
  left: 0;
  background: linear-gradient(90deg, ${BG} 0%, transparent 100%);
}
.lux-carousel::after {
  right: 0;
  background: linear-gradient(270deg, ${BG} 0%, transparent 100%);
}
.lux-carousel-track {
  display: flex;
  gap: 14px;
  width: max-content;
  will-change: transform;
}

/* Flèches navigation carousel */
.lux-carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px; height: 44px;
  border-radius: 50%;
  background: rgba(0,0,0,0.75);
  backdrop-filter: blur(10px);
  border: 1px solid ${BORDER_STRONG};
  color: ${GOLD_LIGHT};
  font-size: 20px;
  cursor: pointer;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  transition: transform .2s ease, background .2s ease, border-color .2s ease;
  box-shadow: 0 4px 16px rgba(0,0,0,0.5);
}
.lux-carousel-nav:hover {
  transform: translateY(-50%) scale(1.08);
  background: rgba(212,175,55,0.15);
  border-color: ${GOLD};
}
.lux-carousel-nav.prev { left: 8px; }
.lux-carousel-nav.next { right: 8px; }
.lux-carousel-card {
  position: relative;
  flex: 0 0 auto;
  width: clamp(160px, 18vw, 230px);
  aspect-ratio: 3 / 4;
  background: #000;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform .3s ease, border-color .3s ease;
}
.lux-carousel-card:hover {
  transform: translateY(-4px) scale(1.02);
  border-color: ${BORDER_STRONG};
}
@keyframes scroll-cards {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

.lux-tag-cover {
  position: absolute;
  top: 16px; left: 16px;
  z-index: 3;
  padding: 6px 14px;
  background: ${GOLD_MIRROR};
  background-size: 200% auto;
  animation: shimmer 5s linear infinite;
  color: #1a1407;
  border-radius: 999px;
  font-family: 'Italiana', 'Bodoni Moda', serif;
  font-size: 11px;
  letter-spacing: 4px;
  text-transform: uppercase;
  font-weight: 600;
}

/* Bandeau premium qui défile à l'infini */
.lux-marquee {
  position: relative;
  overflow: hidden;
  width: 100%;
  background:
    linear-gradient(90deg, #0a0a0a 0%, #1a1407 50%, #0a0a0a 100%);
  border-top: 1px solid rgba(212,175,55,0.35);
  border-bottom: 1px solid rgba(212,175,55,0.35);
  padding: 16px 0;
  box-shadow: 0 0 30px rgba(212,175,55,0.12) inset;
}
.lux-marquee::before,
.lux-marquee::after {
  content: '';
  position: absolute;
  top: 0; bottom: 0;
  width: 90px;
  z-index: 2;
  pointer-events: none;
}
.lux-marquee::before {
  left: 0;
  background: linear-gradient(90deg, #0a0a0a 0%, transparent 100%);
}
.lux-marquee::after {
  right: 0;
  background: linear-gradient(270deg, #0a0a0a 0%, transparent 100%);
}
.lux-marquee-track {
  display: flex;
  width: max-content;
  white-space: nowrap;
  animation: marquee 36s linear infinite;
  will-change: transform;
}
.lux-marquee-track:hover { animation-play-state: paused; }
.lux-marquee-item {
  display: inline-flex;
  align-items: center;
  gap: 22px;
  padding: 0 26px;
  font-family: 'Italiana', 'Bodoni Moda', serif;
  font-size: clamp(13px, 1.6vw, 18px);
  letter-spacing: 6px;
  text-transform: uppercase;
  color: #f4ead0;
}
.lux-marquee-item .star {
  color: ${GOLD_LIGHT};
  font-size: 0.85em;
  text-shadow: 0 0 8px rgba(212,175,55,0.5);
}
@keyframes marquee {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

.lux-corner.tl { top: 10px; left: 10px; border-right: none; border-bottom: none; }
.lux-corner.tr { top: 10px; right: 10px; border-left: none; border-bottom: none; }
.lux-corner.bl { bottom: 10px; left: 10px; border-right: none; border-top: none; }
.lux-corner.br { bottom: 10px; right: 10px; border-left: none; border-top: none; }

/* Range slider luxe */
.lux-range {
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, ${GOLD_DARK}, ${GOLD}, ${GOLD_LIGHT});
  outline: none;
  cursor: pointer;
}
.lux-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px; height: 22px; border-radius: 50%;
  background: ${GOLD_MIRROR};
  background-size: 200% auto;
  animation: shimmer 4s linear infinite;
  border: 2px solid #1a1407;
  box-shadow: 0 4px 12px rgba(212,175,55,0.5), 0 0 0 1px ${GOLD};
  cursor: grab;
  transition: transform .15s ease;
}
.lux-range::-webkit-slider-thumb:active { transform: scale(1.15); cursor: grabbing; }
.lux-range::-moz-range-thumb {
  width: 22px; height: 22px; border-radius: 50%;
  background: ${GOLD};
  border: 2px solid #1a1407;
  box-shadow: 0 4px 12px rgba(212,175,55,0.5);
  cursor: grab;
}
.lux-range.lux-range-silver { background: linear-gradient(90deg, #5a5a5a, ${SILVER}, #ffffff); }
.lux-range.lux-range-silver::-webkit-slider-thumb {
  background: ${SILVER_MIRROR};
  background-size: 200% auto;
  animation: shimmer 5s linear infinite;
  border-color: #111;
  box-shadow: 0 4px 12px rgba(192,192,192,0.5), 0 0 0 1px ${SILVER};
}

/* Select luxe */
select.lux-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, ${GOLD} 50%), linear-gradient(135deg, ${GOLD} 50%, transparent 50%);
  background-position: calc(100% - 16px) center, calc(100% - 11px) center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 32px !important;
}
select.lux-input option { background: ${SURFACE}; color: ${TEXT}; }
`;

// === Helpers globaux ===
const formatAbonnes = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

// === Carousel d'une catégorie (auto-scroll + nav manuelle) ===
function CategoryCarousel({ title, items, onSelect, id }) {
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    let raf;
    const speed = 0.4;
    const tick = () => {
      const el = trackRef.current;
      if (el && !pausedRef.current) {
        offsetRef.current -= speed;
        const halfWidth = el.scrollWidth / 2;
        if (-offsetRef.current >= halfWidth) offsetRef.current += halfWidth;
        el.style.transform = `translateX(${offsetRef.current}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const scrollManual = (dx) => {
    const el = trackRef.current;
    if (!el) return;
    pausedRef.current = true;
    offsetRef.current += dx;
    el.style.transition = 'transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)';
    el.style.transform = `translateX(${offsetRef.current}px)`;
    setTimeout(() => {
      if (!trackRef.current) return;
      trackRef.current.style.transition = '';
      const halfWidth = trackRef.current.scrollWidth / 2;
      if (-offsetRef.current >= halfWidth) offsetRef.current += halfWidth;
      else if (offsetRef.current >= 0) offsetRef.current -= halfWidth;
      trackRef.current.style.transform = `translateX(${offsetRef.current}px)`;
      setTimeout(() => { pausedRef.current = false; }, 2500);
    }, 460);
  };

  if (!items || items.length === 0) return null;
  // Doubler pour boucle infinie
  const doubled = [...items, ...items];

  return (
    <div id={id} style={{ marginBottom: 36, scrollMarginTop: 100 }}>
      <div className="lux-couture" style={{
        textAlign: 'center', fontSize: 12, color: GOLD_LIGHT,
        marginBottom: 14, marginTop: 6,
        letterSpacing: 5
      }}>
        ✦ {title} ✦
      </div>
      <div
        className="lux-carousel"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        <button type="button" className="lux-carousel-nav prev" onClick={() => scrollManual(240)} aria-label="Précédent">‹</button>
        <button type="button" className="lux-carousel-nav next" onClick={() => scrollManual(-240)} aria-label="Suivant">›</button>
        <div className="lux-carousel-track" ref={trackRef}>
          {doubled.map((inf, i) => (
            <div
              key={`${inf.id}-${i}`}
              onClick={() => onSelect(inf)}
              className="lux-carousel-card"
            >
              {inf.photo_url ? (
                <img
                  src={inf.photo_url}
                  alt={inf.nom}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 18%',
                    filter: 'contrast(1.06) saturate(1.05) brightness(1.02)'
                  }}
                />
              ) : (
                <div className="lux-serif lux-mirror-text" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 70, fontWeight: 700 }}>
                  {inf.nom?.charAt(0)}
                </div>
              )}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.92) 100%)',
                pointerEvents: 'none', zIndex: 1
              }}/>
              <div style={{
                position: 'absolute', left: 0, right: 0, bottom: 0,
                padding: '10px 12px 12px',
                zIndex: 2
              }}>
                <h3 className="lux-serif" style={{
                  margin: '0 0 4px',
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#fff',
                  lineHeight: 1.1,
                  letterSpacing: '-0.2px',
                  textTransform: 'uppercase',
                  textShadow: '0 2px 10px rgba(0,0,0,0.85)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>{inf.nom}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="lux-number" style={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>
                    {formatAbonnes(inf.abonnes)}
                  </div>
                  <div style={{ width: 1, height: 12, background: BORDER }}/>
                  <div className="lux-number-silver" style={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>
                    {parseFloat(inf.prix_abo_trimestre).toFixed(0)}€<span style={{ fontSize: '0.75em', fontWeight: 500, color: TEXT_MUTED }}>/m</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// === Slug pour les ID des sections catégorie (utilisé par le scroll au clic sur les pills) ===
function slugifyCat(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// === Contenu interne d'une carte d'égérie (même format que les cartes éditoriales du haut) ===
function InfCardContent({ inf, large = false }) {
  const labelStyle = {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    fontSize: large ? 9 : 8,
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: large ? 2 : 1.5,
    marginBottom: 2,
    lineHeight: 1
  };
  const numberSize = large ? 'clamp(20px, 2.8vw, 32px)' : 'clamp(14px, 1.7vw, 19px)';
  return (
    <>
      {inf.photo_url ? (
        <img
          src={inf.photo_url}
          alt={inf.nom}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: large ? '50% 28%' : '50% 25%',
            filter: 'contrast(1.06) saturate(1.05) brightness(1.02)'
          }}
        />
      ) : (
        <div className="lux-serif lux-mirror-text" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: large ? 200 : 90, fontWeight: 700 }}>
          {inf.nom?.charAt(0)}
        </div>
      )}
      {/* Voile bas pour la lisibilité du texte */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.95) 100%)',
        pointerEvents: 'none', zIndex: 1
      }}/>
      {/* Coins ornés dorés */}
      <span className="lux-corner tl" style={{ zIndex: 3 }}/>
      <span className="lux-corner tr" style={{ zIndex: 3 }}/>
      <span className="lux-corner bl" style={{ zIndex: 3 }}/>
      <span className="lux-corner br" style={{ zIndex: 3 }}/>
      {/* Pseudo en haut si présent (comme cartes éditoriales) */}
      {inf.pseudo && (
        <div className="lux-c-pseudo" style={{
          position: 'absolute', top: large ? 12 : 8, left: large ? 14 : 10, zIndex: 2,
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          fontSize: large ? 10 : 8,
          fontWeight: 600,
          color: GOLD_LIGHT,
          opacity: 0.92,
          letterSpacing: 2,
          textTransform: 'uppercase',
          textShadow: '0 1px 6px rgba(0,0,0,0.9)'
        }}>@{inf.pseudo}</div>
      )}
      {/* Bloc info bas : nom + Audience + Mensuel (même format que les cartes éditoriales) */}
      <div className="lux-c-bottom" style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        padding: large ? 'clamp(14px, 2vw, 22px)' : '12px 12px 14px',
        zIndex: 2
      }}>
        <h3 className="lux-serif lux-c-name" style={{
          margin: '0 0 10px',
          fontSize: large ? 'clamp(20px, 3vw, 38px)' : 'clamp(12px, 1.5vw, 17px)',
          fontWeight: 700,
          color: '#fff',
          lineHeight: 1.1,
          letterSpacing: '-0.3px',
          textTransform: 'uppercase',
          textShadow: '0 2px 14px rgba(0,0,0,0.9)',
          wordBreak: 'break-word',
          hyphens: 'auto'
        }}>{inf.nom}</h3>
        <div className="lux-c-numbers" style={{ display: 'flex', alignItems: 'flex-end', gap: large ? 14 : 10, flexWrap: 'wrap', rowGap: 6, columnGap: large ? 14 : 10 }}>
          <div className="lux-c-num-wrap" style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="lux-c-label" style={labelStyle}>Audience</div>
            <div className="lux-number lux-c-num-audience" style={{ fontSize: numberSize, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
              {formatAbonnes(inf.abonnes)}
            </div>
          </div>
          <div className="lux-c-divider" style={{ width: 1, height: large ? 30 : 22, background: BORDER, flexShrink: 0, alignSelf: 'flex-end', marginBottom: 1 }}/>
          <div className="lux-c-num-wrap" style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="lux-c-label" style={labelStyle}>Mensuel</div>
            <div className="lux-number-silver lux-c-num-price" style={{ fontSize: numberSize, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
              {parseFloat(inf.prix_abo_trimestre).toFixed(0)}€
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// === Layouts mosaïque variés (rotation par catégorie pour donner un effet magazine) ===
const MOSAIC_LAYOUTS = [
  // Layout 0 : grosse top-left + 5 petites (3 cols × 3 rows) — 6 cartes
  {
    count: 6,
    cols: 'repeat(3, 1fr)',
    rows: 'repeat(3, minmax(110px, 1fr))',
    height: 'clamp(420px, 56vw, 620px)',
    cards: [
      { col: '1 / 3', row: '1 / 3', large: true },
      { col: '3', row: '1' },
      { col: '3', row: '2' },
      { col: '1', row: '3' },
      { col: '2', row: '3' },
      { col: '3', row: '3' }
    ]
  },
  // Layout 1 : 2 medium côte à côte + 4 petites en bas (4 cols × 3 rows) — 6 cartes
  {
    count: 6,
    cols: 'repeat(4, 1fr)',
    rows: 'repeat(3, minmax(100px, 1fr))',
    height: 'clamp(380px, 50vw, 560px)',
    cards: [
      { col: '1 / 3', row: '1 / 3', large: true },
      { col: '3 / 5', row: '1 / 3', large: true },
      { col: '1', row: '3' },
      { col: '2', row: '3' },
      { col: '3', row: '3' },
      { col: '4', row: '3' }
    ]
  },
  // Layout 2 : grosse top-right + 5 petites (mirror du 0) — 6 cartes
  {
    count: 6,
    cols: 'repeat(3, 1fr)',
    rows: 'repeat(3, minmax(110px, 1fr))',
    height: 'clamp(420px, 56vw, 620px)',
    cards: [
      { col: '2 / 4', row: '1 / 3', large: true },
      { col: '1', row: '1' },
      { col: '1', row: '2' },
      { col: '1', row: '3' },
      { col: '2', row: '3' },
      { col: '3', row: '3' }
    ]
  },
  // Layout 3 : 1 hero pleine largeur en haut + 4 petites (4 cols × 3 rows) — 5 cartes
  {
    count: 5,
    cols: 'repeat(4, 1fr)',
    rows: 'repeat(3, minmax(110px, 1fr))',
    height: 'clamp(360px, 48vw, 540px)',
    cards: [
      { col: '1 / 5', row: '1 / 3', large: true },
      { col: '1', row: '3' },
      { col: '2', row: '3' },
      { col: '3', row: '3' },
      { col: '4', row: '3' }
    ]
  },
  // Layout 4 : grille 3×2 toutes égales — 6 cartes
  {
    count: 6,
    cols: 'repeat(3, 1fr)',
    rows: 'repeat(2, minmax(160px, 1fr))',
    height: 'clamp(340px, 46vw, 520px)',
    cards: [
      { col: '1', row: '1' },
      { col: '2', row: '1' },
      { col: '3', row: '1' },
      { col: '1', row: '2' },
      { col: '2', row: '2' },
      { col: '3', row: '2' }
    ]
  },
  // Layout 5 : 1 grosse à droite plein haut + 3 petites à gauche (3 cols × 3 rows) — 4 cartes
  {
    count: 4,
    cols: 'repeat(3, 1fr)',
    rows: 'repeat(3, minmax(120px, 1fr))',
    height: 'clamp(380px, 50vw, 560px)',
    cards: [
      { col: '2 / 4', row: '1 / 4', large: true },
      { col: '1', row: '1' },
      { col: '1', row: '2' },
      { col: '1', row: '3' }
    ]
  },
  // Layout 6 : asymétrique — 1 grosse + 4 petites mixtes (3 cols × 3 rows) — 5 cartes
  {
    count: 5,
    cols: 'repeat(3, 1fr)',
    rows: 'repeat(3, minmax(110px, 1fr))',
    height: 'clamp(380px, 50vw, 560px)',
    cards: [
      { col: '1 / 3', row: '1 / 3', large: true },
      { col: '3', row: '1' },
      { col: '3', row: '2' },
      { col: '1', row: '3' },
      { col: '2 / 4', row: '3' }
    ]
  }
];

// === Bloc mosaïque réutilisable (rendu des cartes selon un layout) ===
function MosaicBlock({ items, layoutIdx, onSelect }) {
  const layout = MOSAIC_LAYOUTS[layoutIdx % MOSAIC_LAYOUTS.length];
  const cards = items.slice(0, layout.count);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: layout.cols,
      gridTemplateRows: layout.rows,
      gap: 'clamp(6px, 0.9vw, 12px)',
      height: layout.height,
      marginBottom: 'clamp(8px, 1.2vw, 16px)'
    }}>
      {cards.map((inf, idx) => {
        const place = layout.cards[idx];
        if (!place) return null;
        return (
          <div
            key={inf.id}
            onClick={() => onSelect(inf)}
            className={'lux-card-hover-fx' + (place.large ? '' : ' lux-mini-card')}
            style={{
              position: 'relative',
              gridColumn: place.col,
              gridRow: place.row,
              background: '#000',
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              overflow: 'hidden',
              cursor: 'pointer',
              animation: 'fadeInUp .6s ease both',
              minHeight: 0,
              minWidth: 0
            }}
          >
            <InfCardContent inf={inf} large={!!place.large} />
          </div>
        );
      })}
    </div>
  );
}

// Chunk un tableau d'items en blocs successifs en suivant les counts des layouts
function chunkByLayouts(items, startIdx = 0) {
  const blocks = [];
  let remaining = [...items];
  let idx = startIdx;
  while (remaining.length > 0) {
    // cherche le prochain layout dont le count <= items restants
    let chosen = null;
    for (let i = 0; i < MOSAIC_LAYOUTS.length; i++) {
      const layoutIdx = (idx + i) % MOSAIC_LAYOUTS.length;
      if (MOSAIC_LAYOUTS[layoutIdx].count <= remaining.length) {
        chosen = layoutIdx;
        break;
      }
    }
    if (chosen === null) {
      // moins d'items que le plus petit layout — on crée un bloc "simple grid"
      blocks.push({ items: remaining, layoutIdx: null });
      break;
    }
    const count = MOSAIC_LAYOUTS[chosen].count;
    blocks.push({ items: remaining.slice(0, count), layoutIdx: chosen });
    remaining = remaining.slice(count);
    idx = chosen + 1;
  }
  return blocks;
}

// === Grille / mosaïque pour une catégorie — magazine éditorial avec layouts variés ===
function CategoryGrid({ title, featured, totalCount, onSelect, onSeeAll, id, layoutIdx = 0 }) {
  if (!featured || featured.length === 0) return null;

  const layout = MOSAIC_LAYOUTS[layoutIdx % MOSAIC_LAYOUTS.length];
  // On limite featured au count du layout (pas d'overflow visuel)
  const cards = featured.slice(0, layout.count);
  // Masque le bouton si tout est déjà visible ; sinon affiche
  const totalDisplayed = cards.length;

  return (
    <div id={id} style={{ marginBottom: 64, scrollMarginTop: 100 }}>
      {/* Titre centré */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <h2 className="lux-serif" style={{
          fontSize: 'clamp(26px, 4.5vw, 52px)',
          fontWeight: 700,
          margin: 0,
          color: '#d8ccb8',
          textTransform: 'uppercase',
          letterSpacing: '-0.5px',
          lineHeight: 1.1,
          textShadow: '0 6px 24px rgba(0,0,0,0.6)'
        }}>
          {title}
        </h2>
        <div className="lux-divider" style={{ maxWidth: 80, margin: '14px auto 0' }}/>
      </div>

      {/* Mosaïque magazine — layout variable */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: layout.cols,
        gridTemplateRows: layout.rows,
        gap: 'clamp(6px, 0.9vw, 12px)',
        height: layout.height
      }}>
        {cards.map((inf, idx) => {
          const place = layout.cards[idx];
          if (!place) return null;
          return (
            <div
              key={inf.id}
              onClick={() => onSelect(inf)}
              className={'lux-card-hover-fx' + (place.large ? '' : ' lux-mini-card')}
              style={{
                position: 'relative',
                gridColumn: place.col,
                gridRow: place.row,
                background: '#000',
                border: `1px solid ${BORDER}`,
                borderRadius: 12,
                overflow: 'hidden',
                cursor: 'pointer',
                animation: 'fadeInUp .6s ease both',
                minHeight: 0,
                minWidth: 0
              }}
            >
              <InfCardContent inf={inf} large={!!place.large} />
            </div>
          );
        })}
      </div>

      {/* Bouton Tout voir centré sous la mosaïque */}
      {totalCount > totalDisplayed && (
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <button
            type="button"
            onClick={() => onSeeAll()}
            style={{
              background: 'transparent',
              border: `1px solid ${BORDER_STRONG}`,
              color: GOLD_LIGHT,
              padding: '12px 28px',
              borderRadius: 999,
              fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all .25s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Tout voir ({totalCount}) →
          </button>
        </div>
      )}
    </div>
  );
}

function BoutiqueInfluenceurs() {
  const [influenceurs, setInfluenceurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedInf, setSelectedInf] = useState(null);
  const [orderType, setOrderType] = useState(null);
  const [orderForm, setOrderForm] = useState({
    nom_acheteur: '', email_acheteur: '', telephone_acheteur: '',
    entreprise_acheteur: '', brief: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Modal "Tout voir" pour une catégorie
  const [categoryModal, setCategoryModal] = useState(null); // null | { title, items }

  // Modal formulaire de contact (Contactez-nous / Contact footer)
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ nom: '', email: '', telephone: '', entreprise: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactDone, setContactDone] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (contactSubmitting) return;
    setContactSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('nom', contactForm.nom);
      fd.append('email', contactForm.email);
      fd.append('telephone', contactForm.telephone || '');
      fd.append('entreprise', contactForm.entreprise || '');
      fd.append('message', contactForm.message || '');
      fd.append('source', 'am stram gram');
      const r = await fetch('/api/leads/public', { method: 'POST', body: fd });
      if (!r.ok) throw new Error('Erreur serveur');
      setContactDone(true);
      setContactForm({ nom: '', email: '', telephone: '', entreprise: '', message: '' });
    } catch (err) {
      alert('Une erreur est survenue. Merci de réessayer ou de nous écrire à contact@e-application.fr');
    } finally {
      setContactSubmitting(false);
    }
  };
  const closeContact = () => {
    setContactOpen(false);
    setTimeout(() => setContactDone(false), 300);
  };

  // Index pour rotation des noms d'influenceurs dans le hero
  const [heroInfIndex, setHeroInfIndex] = useState(0);

  // Filtres avancés
  const [showFilters, setShowFilters] = useState(false);
  const [audienceBucket, setAudienceBucket] = useState('all'); // all|micro|mid|macro|mega
  const [platform, setPlatform] = useState('all'); // all|instagram|tiktok|youtube
  const [priceMax, setPriceMax] = useState(null);
  const [priceAboMax, setPriceAboMax] = useState(null);
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    fetch('/api/boutique/influenceurs')
      .then(r => r.json())
      .then(data => { setInfluenceurs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Liste des noms d'influenceurs (avec photo) pour rotation hero
  const heroNames = influenceurs
    .filter(i => i.photo_url && i.nom)
    .map(i => i.nom);

  // Rotation auto toutes les 2.2s
  useEffect(() => {
    if (heroNames.length === 0) return;
    const id = setInterval(() => {
      setHeroInfIndex(i => (i + 1) % heroNames.length);
    }, 2200);
    return () => clearInterval(id);
  }, [heroNames.length]);

  const categories = ['all', ...Array.from(new Set(
    influenceurs.flatMap(i => (i.categorie || '').split(',').map(c => c.trim()))
                .filter(c => c && c !== 'Tout' && c.toUpperCase() !== 'PREMIUM' && c.toUpperCase() !== 'MODE')
  )).sort()];

  // Bornes prix calculées depuis les données
  const maxPostPrice = Math.max(100, ...influenceurs.map(i => parseFloat(i.prix_post) || 0));
  const maxAboPrice = Math.max(100, ...influenceurs.map(i => parseFloat(i.prix_abo_trimestre) || 0));
  const effectivePriceMax = priceMax ?? maxPostPrice;
  const effectivePriceAboMax = priceAboMax ?? maxAboPrice;

  const inAudienceBucket = (n) => {
    if (audienceBucket === 'all') return true;
    if (audienceBucket === 'micro') return n < 10000;
    if (audienceBucket === 'mid') return n >= 10000 && n < 100000;
    if (audienceBucket === 'macro') return n >= 100000 && n < 1000000;
    if (audienceBucket === 'mega') return n >= 1000000;
    return true;
  };

  const filtered = influenceurs.filter(i => {
    const hasPhoto = !!(i.photo_url && String(i.photo_url).trim());
    if (!hasPhoto) return false;
    // Catégorie peut être CSV : "FAMILY & CHILD,MODE & BEAUTÉ,PREMIUM"
    const infCats = (i.categorie || '').split(',').map(c => c.trim()).filter(Boolean);
    const matchCat = filterCat === 'all' ||
      infCats.includes(filterCat) ||
      infCats.includes('Tout');
    const matchSearch = !search ||
      i.nom?.toLowerCase().includes(search.toLowerCase()) ||
      i.pseudo?.toLowerCase().includes(search.toLowerCase());
    const matchAudience = inAudienceBucket(parseInt(i.abonnes) || 0);
    const matchPlatform = platform === 'all' ||
      (platform === 'instagram' && i.instagram) ||
      (platform === 'tiktok' && i.tiktok) ||
      (platform === 'youtube' && i.youtube);
    const matchPricePost = (parseFloat(i.prix_post) || 0) <= effectivePriceMax;
    const matchPriceAbo = (parseFloat(i.prix_abo_trimestre) || 0) <= effectivePriceAboMax;
    return matchCat && matchSearch && matchAudience && matchPlatform && matchPricePost && matchPriceAbo;
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc': return (parseFloat(a.prix_post) || 0) - (parseFloat(b.prix_post) || 0);
      case 'price-desc': return (parseFloat(b.prix_post) || 0) - (parseFloat(a.prix_post) || 0);
      case 'abonnes-desc': return (parseInt(b.abonnes) || 0) - (parseInt(a.abonnes) || 0);
      case 'abonnes-asc': return (parseInt(a.abonnes) || 0) - (parseInt(b.abonnes) || 0);
      case 'nom-asc': return (a.nom || '').localeCompare(b.nom || '');
      default: return 0;
    }
  });

  const resetFilters = () => {
    setFilterCat('all');
    setSearch('');
    setAudienceBucket('all');
    setPlatform('all');
    setPriceMax(null);
    setPriceAboMax(null);
    setSortBy('default');
  };

  const activeFiltersCount =
    (filterCat !== 'all' ? 1 : 0) +
    (audienceBucket !== 'all' ? 1 : 0) +
    (platform !== 'all' ? 1 : 0) +
    (priceMax !== null && priceMax < maxPostPrice ? 1 : 0) +
    (priceAboMax !== null && priceAboMax < maxAboPrice ? 1 : 0) +
    (sortBy !== 'default' ? 1 : 0);

  const handleOrder = (type) => {
    setOrderType(type);
    setConfirmation(null);
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.nom_acheteur || !orderForm.email_acheteur) {
      alert('Nom et email obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/boutique/commande', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          influenceur_id: selectedInf.id,
          type_commande: orderType,
          ...orderForm
        })
      });
      const data = await res.json();
      if (res.ok) {
        setConfirmation(data);
      } else {
        alert(data.error || 'Erreur');
      }
    } catch (e) {
      alert('Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setSelectedInf(null);
    setOrderType(null);
    setConfirmation(null);
    setOrderForm({ nom_acheteur: '', email_acheteur: '', telephone_acheteur: '', entreprise_acheteur: '', brief: '' });
  };

  return (
    <div className="lux-root" style={{
      minHeight: '100vh',
      color: TEXT,
      overflowX: 'hidden',
      background: `
        radial-gradient(ellipse at 20% 10%, rgba(212,175,55,0.08) 0%, transparent 45%),
        radial-gradient(ellipse at 80% 90%, rgba(212,175,55,0.06) 0%, transparent 45%),
        ${BG}`
    }}>
      <style>{LUXURY_CSS}</style>

      {/* === HERO === image en background (fallback), vidéo Vimeo par-dessus, texte au-dessus */}
      <header
        style={{
          position: 'relative',
          minHeight: '100vh',
          width: '100%',
          backgroundImage: 'url(/hero-influenceurs.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#000',
          color: '#fff',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          padding: 0,
          overflow: 'hidden'
        }}
      >
        {/* Vidéo Vimeo en background — photo reste en fallback pendant chargement / si erreur */}
        <div style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0
        }}>
          <iframe
            src="https://player.vimeo.com/video/1188778548?background=1&autoplay=1&loop=1&muted=1&autopause=0&title=0&byline=0&portrait=0&badge=0"
            title="hero-video"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 'max(100vw, calc(100vh * 784 / 1168))',
              height: 'max(100vh, calc(100vw * 1168 / 784))',
              transform: 'translate(-50%, -50%)',
              border: 0,
              pointerEvents: 'none'
            }}
          />
        </div>

        {/* Voile sombre dégradé : foncé en haut → s'éclaircit vers les badges */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.38) 22%, rgba(0,0,0,0.28) 45%, rgba(0,0,0,0.15) 65%, rgba(0,0,0,0.08) 85%, rgba(0,0,0,0.05) 100%)',
          pointerEvents: 'none',
          zIndex: 1
        }}/>

        {/* Halo doré au sommet */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
          background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.20) 0%, transparent 60%)',
          pointerEvents: 'none',
          zIndex: 1
        }}/>

        {/* === Tout le contenu remonté en haut === */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          padding: '32px 18px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'clamp(2px, 0.3vw, 4px)',
            padding: 'clamp(1px, 0.2vw, 3px) clamp(3px, 0.4vw, 6px)',
            border: `1px solid ${BORDER_STRONG}`,
            borderRadius: 999,
            background: '#ffffff',
            boxShadow:
              '0 0 0 1px rgba(212,175,55,0.4), ' +
              '0 8px 28px rgba(0,0,0,0.35)',
            marginBottom: 18,
            position: 'relative'
          }}>
            <img
              src="/logo-eshops-pro.png"
              alt="E-Shops PRO"
              style={{
                height: 'clamp(28px, 4.2vw, 48px)',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <img
              src="/logo-am-stram-gram.png"
              alt="Am Stram Gram"
              style={{
                height: 'clamp(34px, 5vw, 56px)',
                width: 'auto',
                objectFit: 'contain',
                borderRadius: '50%'
              }}
            />
          </div>

          <h1 className="lux-serif" style={{
            fontSize: 'clamp(36px, 9vw, 92px)',
            margin: 0,
            fontWeight: 700,
            lineHeight: 1.05,
            letterSpacing: '-0.5px',
            textTransform: 'uppercase',
            maxWidth: 1100,
            color: '#d8ccb8',
            textShadow: '0 6px 24px rgba(0,0,0,0.75), 0 2px 4px rgba(0,0,0,0.55)'
          }}>
            Communiquez<br/>comme un PRO
          </h1>

          {/* Conteneur à hauteur fixe → le reste du hero ne bouge JAMAIS */}
          <div style={{
            position: 'relative',
            width: '100%',
            height: 'clamp(105px, 18vw, 160px)',
            margin: '0',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            padding: '0 8px 2px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}>
            <div style={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'clamp(0px, 0.3vw, 4px)',
              transition: 'all .35s ease',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              lineHeight: 1
            }}>
              <span style={{
                fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                fontStyle: 'normal',
                fontSize: 'clamp(11px, 1.4vw, 14px)',
                lineHeight: 1,
                color: '#f0e9d4',
                textShadow: '0 2px 14px rgba(0,0,0,0.7)',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                flexShrink: 0,
                fontWeight: 500
              }}>
                avec
              </span>
              <span
                key={heroInfIndex}
                className="lux-signature"
                style={{
                  fontSize: (() => {
                    const len = Math.max((heroNames[heroInfIndex] || '').length, 1);
                    const vw = Math.min(160 / len, 10);
                    const px = Math.min(vw * 13, 80);
                    const minPx = 14;
                    return `clamp(${minPx}px, ${vw.toFixed(2)}vw, ${Math.round(px)}px)`;
                  })(),
                  lineHeight: 1.4,
                  paddingTop: '0.2em',
                  paddingBottom: '0.1em',
                  paddingLeft: '0.45em',
                  paddingRight: '0.25em',
                  animation: 'fadeInUp .5s ease both',
                  color: '#ffffff',
                  WebkitTextStroke: '0',
                  textShadow: '0 6px 24px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.5)',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  transition: 'font-size .3s ease',
                  display: 'inline-block',
                  minWidth: 0,
                  overflow: 'visible'
                }}
              >
                {heroNames.length > 0 ? heroNames[heroInfIndex] : '...'}
              </span>
            </div>
          </div>

          <div className="lux-divider" style={{ maxWidth: 80, margin: '0 auto 18px' }}/>

          {/* Espace réservé (ancienne zone des étapes) */}
          <div style={{ height: 50 }}/>

          {/* CTAs */}
          <div style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: 14,
            width: '100%'
          }}>
            <a
              href="#contact"
              onClick={e => {
                e.preventDefault();
                setContactOpen(true);
              }}
              className="lux-btn-gold"
              style={{
                fontFamily: "'Bodoni Moda', 'Didot', 'Bodoni 72', 'Times New Roman', serif",
                fontWeight: 700,
                color: '#ffffff',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                background: 'linear-gradient(110deg, #4a3208 0%, #6b4810 18%, #8a5e16 38%, #a87420 50%, #8a5e16 62%, #6b4810 82%, #4a3208 100%)',
                backgroundSize: '200% auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '14px 28px',
                fontSize: 'clamp(13px, 2vw, 15px)',
                letterSpacing: '2.5px',
                textTransform: 'uppercase'
              }}
            >
              Contactez-nous
            </a>
            <button
              type="button"
              onClick={() => {
                const grid = document.querySelector('main');
                if (grid && grid.scrollIntoView) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="lux-btn-silver"
              style={{
                fontFamily: "'Bodoni Moda', 'Didot', 'Bodoni 72', 'Times New Roman', serif",
                fontWeight: 700,
                color: '#ffffff',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                background: 'linear-gradient(110deg, #1c1c1c 0%, #333333 25%, #4a4a4a 50%, #333333 75%, #1c1c1c 100%)',
                backgroundSize: '200% auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.15)',
                padding: '14px 28px',
                fontSize: 'clamp(13px, 2vw, 15px)',
                letterSpacing: '2.5px',
                textTransform: 'uppercase'
              }}
            >
              Découvrir
            </button>
          </div>

          <div style={{
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: '#d8d2c0',
            marginTop: 22,
            opacity: 0.85
          }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
      </header>

      {/* Bandeau premium défilant */}
      <div className="lux-marquee">
        <div className="lux-marquee-track">
          {Array.from({ length: 2 }).map((_, dup) => (
            <div key={dup} style={{ display: 'flex' }}>
              {[
                "L'art de la com de star",
                'Site web sur-mesure',
                'Application mobile native',
                "Lancement officiel avec votre égérie",
                'Visibilité d\'exception',
                'Gestion automatisée',
                'Solution clé en main'
              ].map((txt, i) => (
                <span key={i} className="lux-marquee-item">
                  <span className="star">✦</span>
                  <span>{txt}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* === Section Présentation === */}
      <section style={{
        position: 'relative',
        padding: 'clamp(60px, 9vw, 120px) 20px',
        borderBottom: `1px solid ${BORDER}`,
        background: `radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.05) 0%, transparent 60%), ${BG}`
      }}>
        <div className="lux-presentation" style={{
          maxWidth: 1240,
          margin: '0 auto'
        }}>
          {/* Visuel */}
          <div className="lux-pres-photo" style={{
            position: 'relative',
            aspectRatio: '4 / 5',
            borderRadius: 14,
            overflow: 'hidden',
            border: `1px solid ${BORDER_STRONG}`,
            boxShadow: '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,175,55,0.2)'
          }}>
            <img
              src="/hero-influenceurs.png"
              alt="Réseau d'égéries E-Shops Pro"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: 'center 22%',
                filter: 'contrast(1.04) saturate(1.05)'
              }}
            />
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.7) 100%)',
              pointerEvents: 'none'
            }}/>
            <span className="lux-corner tl"/>
            <span className="lux-corner tr"/>
            <span className="lux-corner bl"/>
            <span className="lux-corner br"/>
          </div>

          {/* Label — même style que les petits labels du hero (Inter uppercase letter-spaced) */}
          <div className="lux-pres-label" style={{
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            fontSize: 'clamp(11px, 1.4vw, 14px)',
            fontWeight: 500,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: GOLD_LIGHT
          }}>
            ◆ Notre engagement
          </div>

          {/* Titre — même traitement que le H1 du hero, en blanc */}
          <h2 className="lux-serif lux-pres-title" style={{
            fontSize: 'clamp(28px, 4.5vw, 56px)',
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.5px',
            textTransform: 'uppercase',
            color: '#ffffff',
            margin: 0,
            textShadow: '0 6px 24px rgba(0,0,0,0.75), 0 2px 4px rgba(0,0,0,0.55)'
          }}>
            Votre marque<br/>propulsée au sommet
          </h2>

          {/* Divider */}
          <div className="lux-divider lux-pres-divider" style={{ maxWidth: 80, margin: 0 }}/>

          {/* Paragraphes — même style que la date du hero (Inter 500 uppercase letter-spaced) */}
          <div className="lux-pres-paragraphs">
            <p style={{
              fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
              fontSize: 'clamp(11px, 1.2vw, 13px)',
              fontWeight: 500,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              lineHeight: 2,
              color: '#d8d2c0',
              margin: '0 0 22px'
            }}>
              Imaginez votre marque portée par les égéries que vos clients <strong style={{ color: '#fff', fontWeight: 700 }}>adorent déjà</strong>. Visibilité massive, audiences ultra-engagées — et zéro effort de votre côté.
            </p>

            <p style={{
              fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
              fontSize: 'clamp(11px, 1.2vw, 13px)',
              fontWeight: 500,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              lineHeight: 2,
              color: '#d8d2c0',
              margin: '0 0 22px'
            }}>
              Pendant que les autres misent sur le volume, nous misons sur la <strong style={{ color: '#fff', fontWeight: 700 }}>pertinence</strong>. Chaque égérie est triée sur le volet pour votre univers. Pas de gaspillage, que du ciblage premium.
            </p>

            <p style={{
              fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
              fontSize: 'clamp(11px, 1.2vw, 13px)',
              fontWeight: 500,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              lineHeight: 2,
              color: '#d8d2c0',
              margin: 0
            }}>
              Et on ne s'arrête pas à la campagne : on vous livre <strong style={{ color: '#fff', fontWeight: 700 }}>un arsenal complet</strong> — site e-shops sur-mesure, application mobile native, référencement Google, CRM, automatisation marketing, tunnel de conversion. Tout est clé en main, vous n'avez qu'à faire votre travail.
            </p>
          </div>

          {/* 4 étapes — les badges retirés du hero */}
          <div className="lux-pres-piliers" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 10
          }}>
            {[
              { n: '1', t: 'Création',  s: 'Site + App mobile' },
              { n: '2', t: 'Vous choisissez', s: 'Votre égérie' },
              { n: '3', t: 'Lancement', s: 'Officiel & viral' },
              { n: '4', t: 'Gestion',   s: 'Automatisée' }
            ].map(step => (
              <div key={step.n} style={{
                padding: '14px 12px',
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                textAlign: 'center'
              }}>
                <div className="lux-number" style={{
                  fontSize: 26,
                  fontWeight: 800,
                  lineHeight: 1,
                  marginBottom: 6
                }}>
                  {step.n}
                </div>
                <div style={{
                  fontFamily: "'Bodoni Moda', 'Didot', 'Bodoni 72', 'Times New Roman', serif",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: '#fff'
                }}>
                  {step.t}
                </div>
                <div style={{
                  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                  fontSize: 9,
                  fontWeight: 500,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: '#f0e9d4',
                  marginTop: 4
                }}>
                  {step.s}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filtres */}
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '40px 20px 32px' }}>
        {/* Ligne 1 : catégories + recherche */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => {
                  setFilterCat(cat);
                  const targetId = cat === 'all' ? 'cat-all' : `cat-${slugifyCat(cat)}`;
                  // attendre le re-render après changement de filtre
                  setTimeout(() => {
                    const el = document.getElementById(targetId);
                    if (el && el.scrollIntoView) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 60);
                }}
                className={'lux-pill ' + (filterCat === cat ? 'active' : '')}
              >
                {cat === 'all' ? 'Toutes les égéries' : cat}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="lux-input"
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 240 }}
          />
        </div>

        {/* Ligne 2 : barre actions (filtres avancés + tri + reset) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => setShowFilters(s => !s)}
              className={'lux-pill ' + (showFilters || activeFiltersCount > 0 ? 'active' : '')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <span style={{ fontSize: 14 }}>⚙</span>
              Filtres avancés
              {activeFiltersCount > 0 && (
                <span style={{
                  background: '#1a1407', color: GOLD_LIGHT,
                  borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 700,
                  minWidth: 20, textAlign: 'center'
                }}>{activeFiltersCount}</span>
              )}
            </button>
            {activeFiltersCount > 0 && (
              <button onClick={resetFilters} className="lux-pill" style={{ color: GOLD_LIGHT }}>
                ✕ Réinitialiser
              </button>
            )}
            <span style={{ color: TEXT_MUTED, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase' }}>
              {sorted.length} {sorted.length > 1 ? 'résultats' : 'résultat'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>Trier par</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="lux-input"
              style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer' }}
            >
              <option value="default">Sélection</option>
              <option value="price-asc">Prix croissant</option>
              <option value="price-desc">Prix décroissant</option>
              <option value="abonnes-desc">Audience décroissante</option>
              <option value="abonnes-asc">Audience croissante</option>
              <option value="nom-asc">Nom (A → Z)</option>
            </select>
          </div>
        </div>

        {/* Panneau filtres avancés */}
        {showFilters && (
          <div style={{
            marginTop: 18, padding: '24px 22px',
            background: SURFACE,
            border: `1px solid ${BORDER_STRONG}`,
            borderRadius: 14,
            position: 'relative',
            animation: 'fadeInUp .35s ease both'
          }}>
            <span className="lux-corner tl"/>
            <span className="lux-corner tr"/>
            <span className="lux-corner bl"/>
            <span className="lux-corner br"/>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 28 }}>
              {/* Audience */}
              <div>
                <label style={{ color: GOLD_LIGHT, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                  ◆ Taille d'audience
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {[
                    { v: 'all', l: 'Toutes' },
                    { v: 'micro', l: '< 10K' },
                    { v: 'mid', l: '10K – 100K' },
                    { v: 'macro', l: '100K – 1M' },
                    { v: 'mega', l: '1M+' }
                  ].map(b => (
                    <button
                      key={b.v}
                      onClick={() => setAudienceBucket(b.v)}
                      className={'lux-pill ' + (audienceBucket === b.v ? 'active' : '')}
                      style={{ fontSize: 12, padding: '6px 12px' }}
                    >
                      {b.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Plateforme */}
              <div>
                <label style={{ color: GOLD_LIGHT, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                  ◆ Plateforme
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {[
                    { v: 'all', l: 'Toutes' },
                    { v: 'instagram', l: 'Instagram' },
                    { v: 'tiktok', l: 'TikTok' },
                    { v: 'youtube', l: 'YouTube' }
                  ].map(p => (
                    <button
                      key={p.v}
                      onClick={() => setPlatform(p.v)}
                      className={'lux-pill ' + (platform === p.v ? 'active' : '')}
                      style={{ fontSize: 12, padding: '6px 12px' }}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prix post max */}
              <div>
                <label style={{ color: GOLD_LIGHT, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                  ◆ Prix Pack E-Shops max
                </label>
                <div className="lux-number" style={{ fontSize: 26, fontWeight: 800, marginTop: 8 }}>
                  {Math.round(effectivePriceMax)}€ <span style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 400 }}>HT</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.ceil(maxPostPrice)}
                  step={Math.max(1, Math.floor(maxPostPrice / 100))}
                  value={effectivePriceMax}
                  onChange={e => setPriceMax(parseFloat(e.target.value))}
                  className="lux-range"
                  style={{ width: '100%', marginTop: 10 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: TEXT_MUTED, letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 }}>
                  <span>0€</span>
                  <span>{Math.ceil(maxPostPrice)}€</span>
                </div>
              </div>

              {/* Prix abo max */}
              <div>
                <label style={{ color: GOLD_LIGHT, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                  ◆ Prix abonnement max / mois
                </label>
                <div className="lux-number-silver" style={{ fontSize: 26, fontWeight: 800, marginTop: 8 }}>
                  {Math.round(effectivePriceAboMax)}€ <span style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 400 }}>HT</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.ceil(maxAboPrice)}
                  step={Math.max(1, Math.floor(maxAboPrice / 100))}
                  value={effectivePriceAboMax}
                  onChange={e => setPriceAboMax(parseFloat(e.target.value))}
                  className="lux-range lux-range-silver"
                  style={{ width: '100%', marginTop: 10 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: TEXT_MUTED, letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 }}>
                  <span>0€</span>
                  <span>{Math.ceil(maxAboPrice)}€/mois</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Grille */}
      <main style={{ maxWidth: 1240, margin: '0 auto', padding: '0 20px 80px' }}>
        {loading ? (
          <p className="lux-serif" style={{ textAlign: 'center', padding: 80, fontSize: 22, color: TEXT_MUTED, fontStyle: 'italic' }}>
            Préparation de la collection...
          </p>
        ) : sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, color: TEXT_MUTED }}>
            <p className="lux-serif" style={{ fontSize: 22, fontStyle: 'italic' }}>Aucun talent ne correspond à votre recherche.</p>
            {influenceurs.length === 0 && <p>La collection sera bientôt révélée.</p>}
            {influenceurs.length > 0 && activeFiltersCount > 0 && (
              <button onClick={resetFilters} className="lux-btn-gold" style={{ marginTop: 20 }}>
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <>
            {/* === BLOC TOP : hero à gauche + 2 features empilées à droite === */}
            {sorted[0] && (() => {
              const inf = sorted[0];
              return (
              <div className="lux-edito-top" style={{ gridAutoRows: 'minmax(0, 1fr)' }}>
                {/* === HERO : 1er influenceur, span 2 rows === */}
                <div
                  onClick={() => setSelectedInf(inf)}
                  className="lux-edito-hero lux-card-hover-fx"
                  style={{ aspectRatio: 'auto' }}
                >
                  {inf.photo_url ? (
                    <img
                      src={inf.photo_url}
                      alt={inf.nom}
                      style={{
                        position: 'absolute', inset: 0,
                        width: '100%', height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center 22%',
                        imageRendering: 'auto',
                        filter: 'contrast(1.06) saturate(1.05) brightness(1.02)',
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden'
                      }}
                    />
                  ) : (
                    <div className="lux-serif lux-mirror-text" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 200, fontWeight: 700 }}>
                      {inf.nom?.charAt(0)}
                    </div>
                  )}

                  {/* Petit badge discret en haut-gauche */}
                  <div style={{
                    position: 'absolute', top: 14, left: 14, zIndex: 3,
                    padding: '5px 12px',
                    background: GOLD_MIRROR,
                    backgroundSize: '200% auto',
                    animation: 'shimmer 5s linear infinite',
                    color: '#1a1407',
                    borderRadius: 999,
                    fontFamily: "'Italiana', serif",
                    fontSize: 10,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    fontWeight: 600
                  }}>★ Couverture</div>


                  <span className="lux-corner tl" style={{ zIndex: 3 }}/>
                  <span className="lux-corner tr" style={{ zIndex: 3 }}/>
                  <span className="lux-corner bl" style={{ zIndex: 3 }}/>
                  <span className="lux-corner br" style={{ zIndex: 3 }}/>

                  {/* Voile bas */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.5) 65%, rgba(0,0,0,0.92) 100%)',
                    pointerEvents: 'none', zIndex: 1
                  }}/>

                  {/* Texte bas — stack vertical pour ne jamais déborder */}
                  <div style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0,
                    padding: 'clamp(14px, 2vw, 28px)',
                    zIndex: 2
                  }}>
                    {inf.pseudo && (
                      <div className="lux-couture" style={{ fontSize: 10, color: GOLD_LIGHT, opacity: 0.9, marginBottom: 4 }}>@{inf.pseudo}</div>
                    )}
                    <h3 className="lux-serif" style={{
                      margin: '0 0 10px',
                      fontSize: 'clamp(22px, 4vw, 54px)',
                      fontWeight: 700,
                      color: '#fff',
                      lineHeight: 1,
                      letterSpacing: '-0.5px',
                      textTransform: 'uppercase',
                      textShadow: '0 2px 14px rgba(0,0,0,0.8)',
                      wordBreak: 'break-word'
                    }}>{inf.nom}</h3>
                    {/* Audience + prix abonnement mensuel */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 9, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 2 }}>Audience</div>
                        <div className="lux-number" style={{ fontSize: 'clamp(20px, 2.8vw, 32px)', fontWeight: 800, lineHeight: 1 }}>
                          {formatAbonnes(inf.abonnes)} <span style={{ fontSize: '0.55em', letterSpacing: 1, fontWeight: 600 }}>abonnés</span>
                        </div>
                      </div>
                      <div style={{ width: 1, height: 28, background: BORDER }}/>
                      <div>
                        <div style={{ fontSize: 9, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 2 }}>Mensuel</div>
                        <div className="lux-number-silver" style={{ fontSize: 'clamp(20px, 2.8vw, 32px)', fontWeight: 800, lineHeight: 1 }}>
                          {parseFloat(inf.prix_abo_trimestre).toFixed(0)}€
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* === 2 FEATURES empilées à droite === */}
                {[sorted[1], sorted[2]].filter(Boolean).map(inf => (
                  <div
                    key={inf.id}
                    onClick={() => setSelectedInf(inf)}
                    className="lux-edito-feature lux-card-hover-fx"
                  >
                    {inf.photo_url ? (
                      <img
                        src={inf.photo_url}
                        alt={inf.nom}
                        style={{
                          position: 'absolute', inset: 0,
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center 18%',
                          imageRendering: 'auto',
                          filter: 'contrast(1.06) saturate(1.05) brightness(1.02)',
                          transform: 'translateZ(0)',
                          backfaceVisibility: 'hidden'
                        }}
                      />
                    ) : (
                      <div className="lux-serif lux-mirror-text" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 130, fontWeight: 700 }}>
                        {inf.nom?.charAt(0)}
                      </div>
                    )}

                    {/* Badge "L'Édition" plus discret */}
                    <div style={{
                      position: 'absolute', top: 12, left: 12, zIndex: 3,
                      padding: '4px 10px',
                      background: 'rgba(0,0,0,0.7)',
                      backdropFilter: 'blur(10px)',
                      color: GOLD_LIGHT,
                      border: `1px solid ${BORDER_STRONG}`,
                      borderRadius: 999,
                      fontFamily: "'Italiana', serif",
                      fontSize: 9,
                      letterSpacing: 2.5,
                      textTransform: 'uppercase'
                    }}>L'Édition</div>


                    <span className="lux-corner tl" style={{ zIndex: 3 }}/>
                    <span className="lux-corner tr" style={{ zIndex: 3 }}/>
                    <span className="lux-corner bl" style={{ zIndex: 3 }}/>
                    <span className="lux-corner br" style={{ zIndex: 3 }}/>

                    <div style={{
                      position: 'absolute', inset: 0,
                      // Pour Jazz : voile concentré uniquement autour du texte (bas) — photo visible ailleurs
                      // Pour les autres : voile dégradé classique
                      background: inf.nom === 'Jazz'
                        ? 'radial-gradient(ellipse 130% 65% at 50% 105%, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.15) 55%, transparent 70%)'
                        : 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.95) 100%)',
                      pointerEvents: 'none', zIndex: 1
                    }}/>

                    <div style={{
                      position: 'absolute', left: 0, right: 0, bottom: 0,
                      padding: '14px 16px 16px',
                      zIndex: 2
                    }}>
                      {inf.pseudo && (
                        <div className="lux-couture" style={{ fontSize: 9, color: GOLD_LIGHT, opacity: 0.9, marginBottom: 4 }}>@{inf.pseudo}</div>
                      )}
                      <h3 className="lux-serif" style={{
                        margin: '0 0 8px',
                        fontSize: 'clamp(16px, 2.4vw, 28px)',
                        fontWeight: 700,
                        color: '#fff',
                        lineHeight: 1.05,
                        letterSpacing: '-0.3px',
                        textTransform: 'uppercase',
                        textShadow: '0 2px 12px rgba(0,0,0,0.8)',
                        wordBreak: 'break-word'
                      }}>{inf.nom}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: 8, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 2 }}>Audience</div>
                          <div className="lux-number" style={{ fontSize: 'clamp(14px, 1.8vw, 22px)', fontWeight: 800, lineHeight: 1 }}>
                            {formatAbonnes(inf.abonnes)} <span style={{ fontSize: '0.55em', letterSpacing: 1, fontWeight: 600 }}>abonnés</span>
                          </div>
                        </div>
                        <div style={{ width: 1, height: 20, background: BORDER }}/>
                        <div>
                          <div style={{ fontSize: 8, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 2 }}>Mensuel</div>
                          <div className="lux-number-silver" style={{ fontSize: 'clamp(14px, 1.8vw, 22px)', fontWeight: 800, lineHeight: 1 }}>
                            {parseFloat(inf.prix_abo_trimestre).toFixed(0)}€
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}

            {/* === CAROUSEL + GRILLES par catégorie === */}
            {sorted.length > 3 && (() => {
              // Carousel global : exclut hero+features pour éviter la duplication visuelle
              const carouselPool = sorted.slice(3);
              // Grilles catégories : INCLUT tous (Nabilla doit apparaître dans PREMIUM, etc.)
              const fullPool = sorted;
              const splitCats = (c) => (c || '').split(',').map(s => s.trim()).filter(Boolean);
              const ORDER = ['HOME & DECO / COOK', 'MODE & BEAUTÉ', 'FAMILY & CHILD', 'GOOD VIBES', 'LIFESTYLE', 'MICRO'];
              // Catégories cachées : "Mode" seul (pas "MODE & BEAUTÉ"), "Tout", "PREMIUM"
              const isHidden = (c) => {
                const u = c.toUpperCase();
                return u === 'TOUT' || u === 'PREMIUM' || u === 'MODE';
              };
              const allCats = new Set();
              fullPool.forEach(i => splitCats(i.categorie).forEach(c => {
                if (!isHidden(c)) allCats.add(c);
              }));
              const cats = ORDER.filter(c => allCats.has(c))
                .concat([...allCats].filter(c => !ORDER.includes(c)).sort());

              return (
                <>
                  <CategoryCarousel
                    id="cat-all"
                    title="Toutes les égéries"
                    items={carouselPool}
                    onSelect={setSelectedInf}
                  />

                  {/* === BANDEAU SPÉCIAL RESTAURATEURS — entièrement cliquable === */}
                  <a
                    href="https://eats.e-application.fr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      position: 'relative',
                      display: 'block',
                      margin: '20px 0 36px',
                      padding: 'clamp(28px, 4vw, 48px) clamp(20px, 3vw, 40px)',
                      borderRadius: 18,
                      border: `1px solid ${BORDER_STRONG}`,
                      background: `
                        radial-gradient(ellipse at 0% 0%, rgba(212,175,55,0.18) 0%, transparent 50%),
                        radial-gradient(ellipse at 100% 100%, rgba(212,175,55,0.12) 0%, transparent 55%),
                        linear-gradient(135deg, #0e0e0e 0%, #1a1407 50%, #0e0e0e 100%)
                      `,
                      overflow: 'hidden',
                      textAlign: 'center',
                      textDecoration: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      transition: 'transform .35s ease, box-shadow .35s ease, border-color .35s ease'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.borderColor = GOLD;
                      e.currentTarget.style.boxShadow = '0 18px 50px rgba(0,0,0,0.55), 0 0 60px rgba(212,175,55,0.18)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.borderColor = BORDER_STRONG;
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    {/* Vidéo Vimeo en background — couvre toute la zone (cover) */}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      overflow: 'hidden',
                      pointerEvents: 'none',
                      zIndex: 0,
                      borderRadius: 18
                    }}>
                      <iframe
                        src="https://player.vimeo.com/video/1188799890?background=1&autoplay=1&loop=1&muted=1&autopause=0&title=0&byline=0&portrait=0&badge=0"
                        title="see-eats-video"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        referrerPolicy="strict-origin-when-cross-origin"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '100%',
                          height: 'auto',
                          minWidth: '100%',
                          minHeight: '100%',
                          aspectRatio: '784 / 1168',
                          border: 0,
                          pointerEvents: 'none'
                        }}
                      />
                    </div>

                    {/* Voile sombre par-dessus la vidéo pour la lisibilité */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.40) 50%, rgba(0,0,0,0.55) 100%)',
                      pointerEvents: 'none',
                      zIndex: 0,
                      borderRadius: 18
                    }}/>

                    <span className="lux-corner tl" style={{ zIndex: 1 }}/>
                    <span className="lux-corner tr" style={{ zIndex: 1 }}/>
                    <span className="lux-corner bl" style={{ zIndex: 1 }}/>
                    <span className="lux-corner br" style={{ zIndex: 1 }}/>

                    {/* Grille fine or */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundImage:
                        'linear-gradient(transparent 95%, rgba(212,175,55,0.06) 100%), ' +
                        'linear-gradient(90deg, transparent 95%, rgba(212,175,55,0.06) 100%)',
                      backgroundSize: '40px 40px',
                      pointerEvents: 'none',
                      opacity: 0.5,
                      zIndex: 1
                    }}/>

                    <div style={{ position: 'relative', maxWidth: 760, margin: '0 auto', zIndex: 2 }}>
                      {/* Eyebrow */}
                      <div style={{
                        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                        fontSize: 11,
                        fontWeight: 500,
                        color: GOLD_LIGHT,
                        letterSpacing: 4,
                        textTransform: 'uppercase',
                        marginBottom: 14,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '6px 14px',
                        border: `1px solid ${BORDER_STRONG}`,
                        borderRadius: 999,
                        background: 'rgba(0,0,0,0.4)'
                      }}>
                        ★ Gratuit ★
                      </div>

                      {/* Titre — blanc */}
                      <h2 className="lux-serif" style={{
                        margin: '8px 0 12px',
                        fontSize: 'clamp(24px, 3.6vw, 44px)',
                        fontWeight: 800,
                        lineHeight: 1.1,
                        color: '#ffffff',
                        textShadow: '0 2px 10px rgba(0,0,0,0.7), 0 6px 24px rgba(0,0,0,0.5)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Spécialement pour les restaurateurs
                      </h2>

                      {/* Sous-titre or — avec logo See Eats inline (toujours sur 1 ligne) */}
                      <div className="lux-serif" style={{
                        fontSize: 'clamp(13px, 2.2vw, 28px)',
                        fontWeight: 700,
                        margin: '8px 0 16px',
                        letterSpacing: '0.5px',
                        color: GOLD_LIGHT,
                        textTransform: 'uppercase',
                        textShadow: '0 1px 0 rgba(0,0,0,0.4), 0 0 8px rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'clamp(6px, 1vw, 14px)',
                        flexWrap: 'nowrap',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }}>
                        <span>Brillez sur</span>
                        <img
                          src="/logo-see-eats.png"
                          alt="See Eats"
                          style={{
                            height: 'clamp(70px, 14vw, 200px)',
                            width: 'auto',
                            verticalAlign: 'middle',
                            // Halo blanc lumineux + glow doré pour décoller du fond sombre
                            filter:
                              'brightness(1.18) contrast(1.05) ' +
                              'drop-shadow(0 0 10px rgba(255,255,255,0.6)) ' +
                              'drop-shadow(0 0 20px rgba(212,175,55,0.4)) ' +
                              'drop-shadow(0 6px 18px rgba(0,0,0,0.7))',
                            display: 'inline-block',
                            flexShrink: 0
                          }}
                        />
                        <span>pendant 5 ans</span>
                      </div>

                      {/* Condition */}
                      <p style={{
                        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                        fontSize: 'clamp(11px, 1.2vw, 13px)',
                        fontWeight: 500,
                        letterSpacing: '3px',
                        textTransform: 'uppercase',
                        color: '#ffffff',
                        textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                        margin: '0 auto 14px',
                        maxWidth: 620,
                        lineHeight: 2
                      }}>
                        Pour tout restaurateur ayant commandé une <b style={{ color: GOLD_LIGHT, fontWeight: 700 }}>vidéo d'influenceur</b>.
                      </p>

                      {/* Punchline */}
                      <p style={{
                        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                        fontSize: 'clamp(10px, 1.1vw, 12px)',
                        fontWeight: 500,
                        letterSpacing: '3px',
                        textTransform: 'uppercase',
                        color: '#ffffff',
                        textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                        margin: '0 auto 24px',
                        maxWidth: 580,
                        lineHeight: 2
                      }}>
                        Profitez-en avant le lancement officiel de{' '}
                        <b style={{ fontStyle: 'normal', letterSpacing: 1 }}>
                          <span style={{ color: GOLD_LIGHT }}>SEE</span>{' '}
                          <span style={{ color: '#e8e8e8' }}>EATS</span>
                        </b>
                        {' '}— la prochaine référence des bonnes adresses.
                      </p>

                      {/* CTA — visuel uniquement, le clic va sur le parent <a> */}
                      <span
                        className="lux-btn-gold"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '14px 32px',
                          fontSize: 'clamp(13px, 2vw, 15px)',
                          letterSpacing: 2.5,
                          textTransform: 'uppercase'
                        }}
                      >
                        Découvrir See Eats
                      </span>

                      {/* Note discrète */}
                      <div style={{
                        fontSize: 10,
                        color: '#ffffff',
                        letterSpacing: 1.5,
                        textTransform: 'uppercase',
                        textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                        marginTop: 16,
                        opacity: 0.92
                      }}>
                        Places limitées — Sans engagement
                      </div>
                    </div>
                  </a>

                  {(() => {
                    // Tracker des IDs déjà utilisés dans une mosaïque précédente
                    // pour éviter qu'un influenceur apparaisse deux fois en couverture
                    const usedIds = new Set();
                    // Exclure les 3 top égéries déjà affichées dans le layout éditorial
                    // en haut de page (sorted[0], sorted[1], sorted[2])
                    [sorted[0], sorted[1], sorted[2]].forEach(i => {
                      if (i && i.id != null) usedIds.add(i.id);
                    });
                    return cats.map((cat, catIdx) => {
                      const allItems = fullPool.filter(i =>
                        splitCats(i.categorie).includes(cat) || splitCats(i.categorie).includes('Tout')
                      );
                      if (allItems.length === 0) return null;
                      // Trie par audience décroissante puis filtre les déjà utilisés ailleurs
                      const sortedByFollowers = [...allItems].sort(
                        (a, b) => (parseInt(b.abonnes) || 0) - (parseInt(a.abonnes) || 0)
                      );
                      const featured = sortedByFollowers
                        .filter(i => !usedIds.has(i.id))
                        .slice(0, 6);
                      // Marque les featured comme utilisés pour les sections suivantes
                      featured.forEach(i => usedIds.add(i.id));
                      if (featured.length === 0) return null;
                      return (
                        <CategoryGrid
                          key={cat}
                          id={`cat-${slugifyCat(cat)}`}
                          title={cat}
                          featured={featured}
                          totalCount={allItems.length}
                          onSelect={setSelectedInf}
                          onSeeAll={() => setCategoryModal({ title: cat, items: allItems })}
                          layoutIdx={catIdx}
                        />
                      );
                    });
                  })()}
                </>
              );
            })()}
          </>
        )}
      </main>

      {/* Modal */}
      {/* === Modal "Tout voir" pour une catégorie === */}
      {categoryModal && (
        <>
          {/* Bouton retour — placé HORS du wrapper pour rester position:fixed sur le viewport
              (le wrapper a backdrop-filter qui créerait un containing block) */}
          <button
            onClick={() => setCategoryModal(null)}
            style={{
              position: 'fixed', top: 16, left: 16,
              background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              color: GOLD_LIGHT,
              border: `1px solid ${BORDER_STRONG}`,
              padding: '10px 18px',
              borderRadius: 999,
              fontSize: 11, fontWeight: 500,
              letterSpacing: 2.5,
              textTransform: 'uppercase',
              cursor: 'pointer',
              zIndex: 1100,
              fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
              display: 'inline-flex', alignItems: 'center', gap: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.6), 0 0 16px rgba(212,175,55,0.15)',
              transition: 'all .25s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.18)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.85)'; }}
            aria-label="Retour"
          >
            <span style={{ fontSize: 14 }}>←</span> Retour
          </button>

          <div
            onClick={() => setCategoryModal(null)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              zIndex: 999, padding: 0, overflow: 'auto'
            }}
          >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: BG,
              width: '100%',
              minHeight: '100vh',
              position: 'relative',
              padding: 'clamp(80px, 10vw, 120px) clamp(20px, 3vw, 60px) 60px',
              backgroundImage: `radial-gradient(ellipse at top, rgba(212,175,55,0.06) 0%, transparent 60%)`
            }}
          >
            {/* En-tête catégorie */}
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div className="lux-couture" style={{ fontSize: 11, color: GOLD_LIGHT, letterSpacing: 5, marginBottom: 8 }}>
                ✦ Catégorie ✦
              </div>
              <h2 className="lux-serif" style={{
                fontSize: 'clamp(28px, 5vw, 56px)',
                fontWeight: 700,
                margin: 0,
                color: '#d8ccb8',
                textTransform: 'uppercase',
                letterSpacing: '-0.5px',
                textShadow: '0 6px 24px rgba(0,0,0,0.6)'
              }}>{categoryModal.title}</h2>
              <div style={{
                fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                fontSize: 12, fontWeight: 500, letterSpacing: 3,
                textTransform: 'uppercase', color: TEXT_MUTED,
                marginTop: 12
              }}>
                {categoryModal.items.length} {categoryModal.items.length > 1 ? 'égéries' : 'égérie'}
              </div>
              <div className="lux-divider" style={{ maxWidth: 80, margin: '20px auto 0' }}/>
            </div>

            {/* Mosaïques magazine — même disposition que la home, layouts variés */}
            <div style={{ maxWidth: 1240, margin: '0 auto' }}>
              {chunkByLayouts(categoryModal.items, 0).map((block, blockIdx) => {
                const onSelect = (inf) => {
                  setCategoryModal(null);
                  setSelectedInf(inf);
                };
                if (block.layoutIdx === null) {
                  // Reste partiel : grille simple alignée à gauche
                  return (
                    <div key={blockIdx} style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: 'clamp(8px, 1vw, 14px)',
                      marginBottom: 'clamp(8px, 1.2vw, 16px)'
                    }}>
                      {block.items.map(inf => (
                        <div
                          key={inf.id}
                          onClick={() => onSelect(inf)}
                          className="lux-card-hover-fx lux-mini-card"
                          style={{
                            position: 'relative',
                            aspectRatio: '3 / 4',
                            background: '#000',
                            border: `1px solid ${BORDER}`,
                            borderRadius: 12,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            animation: 'fadeInUp .5s ease both'
                          }}
                        >
                          <InfCardContent inf={inf} />
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <MosaicBlock
                    key={blockIdx}
                    items={block.items}
                    layoutIdx={block.layoutIdx}
                    onSelect={onSelect}
                  />
                );
              })}
            </div>
          </div>
          </div>
        </>
      )}

      {selectedInf && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 0, overflow: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: BG,
            width: '100%',
            minHeight: '100vh',
            position: 'relative',
            backgroundImage: `radial-gradient(ellipse at top, rgba(212,175,55,0.08) 0%, transparent 60%)`
          }}>
            <button onClick={closeModal} style={{
              position: 'fixed', top: 4, left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(10px)',
              color: GOLD_LIGHT, border: `1px solid ${BORDER_STRONG}`,
              width: 36, height: 36, borderRadius: '50%',
              fontSize: 20, cursor: 'pointer', zIndex: 10,
              fontFamily: 'inherit',
              lineHeight: 1,
              padding: 0,
              boxShadow: '0 4px 16px rgba(0,0,0,0.5), 0 0 16px rgba(212,175,55,0.2)'
            }}>×</button>

            {confirmation ? (
              <div style={{ padding: '60px 24px', textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
                <div className="lux-serif lux-mirror-text" style={{ fontSize: 80, marginBottom: 16 }}>✓</div>
                <h2 className="lux-serif lux-mirror-text" style={{ fontSize: 42, fontWeight: 600, margin: 0 }}>Commande confirmée</h2>
                <div className="lux-divider" style={{ maxWidth: 120, margin: '24px auto' }}/>
                <p className="lux-serif" style={{ color: TEXT, fontSize: 20, fontStyle: 'italic' }}>
                  Votre commande pour <b style={{ color: '#e8e8e8', fontStyle: 'normal', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-0.3px', textShadow: '0 1px 0 rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.5)' }}>{confirmation.influenceur}</b> a bien été enregistrée.
                </p>
                <p style={{ color: TEXT_MUTED, marginTop: 12, fontSize: 14, letterSpacing: 1 }}>
                  MONTANT — <b style={{ color: GOLD_LIGHT }}>{confirmation.montant} €</b>
                </p>
                <p style={{ color: TEXT_MUTED, marginTop: 24, fontSize: 13, lineHeight: 1.7 }}>
                  Vous recevrez un email de confirmation et notre équipe vous contactera sous 24h pour finaliser votre commande.
                </p>
                <button onClick={closeModal} className="lux-btn-gold" style={{ marginTop: 32 }}>
                  Retour à la collection
                </button>
              </div>
            ) : !orderType ? (
              <div className="lux-modal-grid">
                {/* Photo compacte */}
                <div style={{ position: 'relative', background: '#000', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                  {(selectedInf.photo_full_url || selectedInf.photo_url) ? (
                    <img
                      src={selectedInf.photo_full_url || selectedInf.photo_url}
                      alt={selectedInf.nom}
                      className="lux-modal-photo"
                    />
                  ) : (
                    <div className="lux-serif lux-mirror-text" style={{ padding: 60, fontSize: 140, fontWeight: 700 }}>
                      {selectedInf.nom?.charAt(0)}
                    </div>
                  )}
                  <span className="lux-corner tl"/>
                  <span className="lux-corner tr"/>
                  <span className="lux-corner bl"/>
                  <span className="lux-corner br"/>
                </div>

                {/* Infos compactes */}
                <div style={{ padding: '24px 20px 32px', maxWidth: 720, margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                    {selectedInf.categorie && (
                      <span style={{
                        padding: '5px 12px',
                        background: GOLD_MIRROR, backgroundSize: '200% auto',
                        animation: 'shimmer 5s linear infinite',
                        color: '#1a1407',
                        borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase'
                      }}>{selectedInf.categorie}</span>
                    )}
                  </div>
                  <h2 className="lux-serif" style={{
                    margin: '6px 0 4px',
                    fontSize: 'clamp(32px, 5vw, 48px)',
                    fontWeight: 800,
                    lineHeight: 1.05,
                    textTransform: 'uppercase',
                    letterSpacing: '-0.5px',
                    color: '#e8e8e8',
                    textShadow: '0 1px 0 rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.5), 0 0 20px rgba(220,220,220,0.15)'
                  }}>
                    {selectedInf.nom}
                  </h2>
                  {selectedInf.pseudo && <div style={{ color: TEXT_MUTED, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase' }}>@{selectedInf.pseudo}</div>}

                  <div className="lux-divider"/>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                    <div style={{ padding: '10px 14px', background: SURFACE, borderRadius: 10, border: `1px solid ${BORDER}` }}>
                      <div style={{ color: TEXT_MUTED, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase' }}>Audience</div>
                      <b className="lux-serif lux-mirror-text" style={{ fontSize: 22 }}>{formatAbonnes(selectedInf.abonnes)}</b>
                    </div>
                    {selectedInf.instagram && <div style={{ padding: '10px 14px', background: SURFACE, borderRadius: 10, border: `1px solid ${BORDER}`, color: '#d8d2c0', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontWeight: 500, fontSize: 12, letterSpacing: '3px', textTransform: 'uppercase' }}><span style={{ color: GOLD }}>◆</span> {selectedInf.instagram}</div>}
                    {selectedInf.tiktok && <div style={{ padding: '10px 14px', background: SURFACE, borderRadius: 10, border: `1px solid ${BORDER}`, color: '#d8d2c0', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontWeight: 500, fontSize: 12, letterSpacing: '3px', textTransform: 'uppercase' }}><span style={{ color: GOLD }}>◆</span> {selectedInf.tiktok}</div>}
                    {selectedInf.youtube && <div style={{ padding: '10px 14px', background: SURFACE, borderRadius: 10, border: `1px solid ${BORDER}`, color: '#d8d2c0', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontWeight: 500, fontSize: 12, letterSpacing: '3px', textTransform: 'uppercase' }}><span style={{ color: GOLD }}>◆</span> {selectedInf.youtube}</div>}
                  </div>

                  {selectedInf.bio && (
                    <div style={{
                      position: 'relative',
                      background: 'linear-gradient(180deg, rgba(212,175,55,0.04) 0%, rgba(0,0,0,0.25) 100%)',
                      padding: 'clamp(28px, 4vw, 44px) clamp(24px, 3.5vw, 38px)',
                      borderRadius: 14,
                      border: `1px solid ${BORDER_STRONG}`,
                      marginBottom: 24,
                      overflow: 'hidden'
                    }}>
                      {/* Coins ornés */}
                      <span className="lux-corner tl"/>
                      <span className="lux-corner tr"/>
                      <span className="lux-corner bl"/>
                      <span className="lux-corner br"/>

                      {/* Guillemet ouvrant ornemental géant en haut-gauche */}
                      <div className="lux-serif" style={{
                        position: 'absolute',
                        top: -12,
                        left: 14,
                        fontSize: 'clamp(80px, 10vw, 130px)',
                        lineHeight: 1,
                        color: GOLD,
                        opacity: 0.35,
                        fontWeight: 700,
                        fontStyle: 'italic',
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}>“</div>

                      {/* Texte de la bio — même style que les paragraphes de la section présentation */}
                      <div style={{
                        position: 'relative',
                        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                        fontWeight: 500,
                        fontSize: 'clamp(11px, 1.2vw, 13px)',
                        lineHeight: 2,
                        color: '#d8d2c0',
                        letterSpacing: '3px',
                        textTransform: 'uppercase',
                        textShadow: '0 1px 3px rgba(0,0,0,0.7), 0 2px 12px rgba(0,0,0,0.45)',
                        textAlign: 'left',
                        zIndex: 1,
                        whiteSpace: 'pre-line'
                      }}>
                        {selectedInf.bio}
                      </div>

                      {/* Guillemet fermant ornemental en bas-droite */}
                      <div className="lux-serif" style={{
                        position: 'absolute',
                        bottom: -38,
                        right: 14,
                        fontSize: 'clamp(80px, 10vw, 130px)',
                        lineHeight: 1,
                        color: GOLD,
                        opacity: 0.35,
                        fontWeight: 700,
                        fontStyle: 'italic',
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}>”</div>
                    </div>
                  )}

                  {/* Inclus */}
                  <div style={{ marginBottom: 22, padding: 22, background: SURFACE, border: `1px solid ${BORDER_STRONG}`, borderRadius: 14, position: 'relative' }}>
                    <span className="lux-corner tl"/>
                    <span className="lux-corner tr"/>
                    <span className="lux-corner bl"/>
                    <span className="lux-corner br"/>
                    <h3 className="lux-serif" style={{
                      margin: '0 0 16px',
                      fontSize: 22,
                      textAlign: 'center',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '-0.3px',
                      color: '#e8e8e8',
                      textShadow: '0 1px 0 rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.5), 0 0 20px rgba(220,220,220,0.15)'
                    }}>
                      Votre pack
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                      {[
                        'Site internet professionnel',
                        'Application mobile',
                        <>Vidéo postée par <b style={{ color: GOLD_LIGHT }}>{selectedInf.nom}</b></>,
                        <>Vidéo postée sur <b>vos réseaux</b></>,
                        'Système de vente en ligne',
                        "Formulaire d'acquisition clients"
                      ].map((item, i) => (
                        <div key={i} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          color: '#d8d2c0',
                          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                          fontSize: 12,
                          fontWeight: 500,
                          letterSpacing: '3px',
                          textTransform: 'uppercase',
                          lineHeight: 1.6
                        }}>
                          <span style={{ color: GOLD_LIGHT, fontSize: 16 }}>✦</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Pack + Abonnement (offre combinée) */}
                  <div className="lux-cta-combo">
                    <button onClick={() => handleOrder('post')} className="lux-btn-gold">
                      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.85 }}>Pack E-Shops</div>
                      <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4, letterSpacing: 1, textTransform: 'uppercase' }}>À partir de</div>
                      <div className="lux-serif" style={{ fontSize: 32, fontWeight: 700, marginTop: 2, display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                        {(parseFloat(selectedInf.prix_post) / 4).toFixed(0)}€
                        <span style={{ fontSize: 14, opacity: 0.85, fontWeight: 600 }}>×4</span>
                        <span style={{ fontSize: 13, opacity: 0.75 }}>HT</span>
                      </div>
                      <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4, letterSpacing: 1.5, textTransform: 'uppercase' }}>Paiement en 4 fois</div>
                    </button>

                    <span className="lux-cta-plus" aria-hidden="true">+</span>

                    <button onClick={() => handleOrder('abonnement')} className="lux-btn-silver">
                      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.85 }}>Abonnement mensuel</div>
                      <div className="lux-serif" style={{ fontSize: 32, fontWeight: 700, marginTop: 4 }}>
                        {parseFloat(selectedInf.prix_abo_trimestre).toFixed(0)}€ <span style={{ fontSize: 13, opacity: 0.75 }}>HT/mois</span>
                      </div>
                      <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4, letterSpacing: 1.5, textTransform: 'uppercase' }}>Engagement 60 mois</div>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={submitOrder} style={{ padding: '32px 22px 48px', maxWidth: 720, margin: '0 auto' }}>
                <button type="button" onClick={() => setOrderType(null)} style={{ background: 'none', border: 'none', color: TEXT_MUTED, cursor: 'pointer', marginBottom: 18, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', fontFamily: 'inherit' }}>
                  ← Retour
                </button>
                <h2 className="lux-serif lux-mirror-text" style={{ margin: '0 0 6px', fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 600 }}>
                  {orderType === 'post' ? 'Commander le Pack E-Shops' : 'S\'abonner mensuellement'}
                </h2>
                <p className="lux-serif" style={{ color: TEXT, marginBottom: 28, fontSize: 18, fontStyle: 'italic' }}>
                  Avec <b style={{ color: '#e8e8e8', fontStyle: 'normal', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-0.3px', textShadow: '0 1px 0 rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.5)' }}>{selectedInf.nom}</b> —{' '}
                  {orderType === 'post' ? (
                    <b>À partir de {(parseFloat(selectedInf.prix_post) / 4).toFixed(2)} € HT ×4</b>
                  ) : (
                    <b>{parseFloat(selectedInf.prix_abo_trimestre).toFixed(2)} € HT / mois — engagement 60 mois</b>
                  )}
                </p>

                <div className="lux-form-grid">
                  <div>
                    <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>Nom complet *</label>
                    <input type="text" required value={orderForm.nom_acheteur} onChange={e => setOrderForm({ ...orderForm, nom_acheteur: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                  </div>
                  <div>
                    <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>Email *</label>
                    <input type="email" required value={orderForm.email_acheteur} onChange={e => setOrderForm({ ...orderForm, email_acheteur: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                  </div>
                  <div>
                    <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>Téléphone</label>
                    <input type="tel" value={orderForm.telephone_acheteur} onChange={e => setOrderForm({ ...orderForm, telephone_acheteur: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                  </div>
                  <div>
                    <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>Entreprise</label>
                    <input type="text" value={orderForm.entreprise_acheteur} onChange={e => setOrderForm({ ...orderForm, entreprise_acheteur: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                  </div>
                  <div className="full">
                    <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>Brief / message</label>
                    <textarea rows={4} value={orderForm.brief} onChange={e => setOrderForm({ ...orderForm, brief: e.target.value })} placeholder="Décrivez ce que vous souhaitez (produit, message, dates...)" className="lux-input" style={{ width: '100%', marginTop: 6, resize: 'vertical' }} />
                  </div>
                </div>

                <button type="submit" disabled={submitting} className={orderType === 'post' ? 'lux-btn-gold' : 'lux-btn-silver'} style={{ marginTop: 24, width: '100%', padding: 18, fontSize: 16, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'wait' : 'pointer' }}>
                  {submitting ? 'Validation...' : 'Valider la commande'}
                </button>
                <p style={{ fontSize: 11, color: TEXT_MUTED, textAlign: 'center', marginTop: 16, letterSpacing: 1, lineHeight: 1.7 }}>
                  En validant, vos informations sont enregistrées et notre équipe<br/>vous contactera pour le paiement.
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {/* === Modal formulaire de contact === */}
      {contactOpen && (
        <div
          onClick={closeContact}
          className="lux-contact-wrap"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: 20, overflow: 'auto'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="lux-contact-card"
            style={{
              background: BG,
              maxWidth: 560,
              width: '100%',
              borderRadius: 16,
              border: `1px solid ${BORDER_STRONG}`,
              padding: 'clamp(28px, 4vw, 44px)',
              position: 'relative',
              boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 60px rgba(212,175,55,0.12)'
            }}
          >
            <span className="lux-corner tl"/>
            <span className="lux-corner tr"/>
            <span className="lux-corner bl"/>
            <span className="lux-corner br"/>

            <button
              onClick={closeContact}
              aria-label="Fermer"
              style={{
                position: 'absolute', top: 14, right: 14,
                background: 'rgba(0,0,0,0.6)',
                color: GOLD_LIGHT,
                border: `1px solid ${BORDER_STRONG}`,
                width: 32, height: 32, borderRadius: '50%',
                fontSize: 18, cursor: 'pointer',
                fontFamily: 'inherit', lineHeight: 1, padding: 0,
                zIndex: 2
              }}
            >×</button>

            {!contactDone ? (
              <>
                <div style={{
                  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                  fontSize: 11, fontWeight: 600,
                  letterSpacing: 4, textTransform: 'uppercase',
                  color: GOLD_LIGHT, marginBottom: 14
                }}>
                  ◆ Contact
                </div>
                <h2 className="lux-serif" style={{
                  fontSize: 'clamp(24px, 3.5vw, 38px)',
                  fontWeight: 700,
                  margin: '0 0 8px',
                  lineHeight: 1.1,
                  letterSpacing: '-0.4px',
                  textTransform: 'uppercase',
                  color: '#ffffff'
                }}>
                  Parlons de votre marque
                </h2>
                <div className="lux-divider" style={{ maxWidth: 60, margin: '0 0 20px' }}/>
                <p style={{
                  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                  fontSize: 13, fontWeight: 400,
                  color: '#d8d2c0', lineHeight: 1.7,
                  margin: '0 0 24px'
                }}>
                  Laissez-nous vos coordonnées et notre équipe vous recontacte sous 24h pour étudier votre projet.
                </p>

                <form onSubmit={submitContact}>
                  <div style={{ display: 'grid', gap: 14 }}>
                    <div>
                      <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Nom complet *</label>
                      <input type="text" required value={contactForm.nom} onChange={e => setContactForm({ ...contactForm, nom: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                    </div>
                    <div>
                      <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Email *</label>
                      <input type="email" required value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                      <div>
                        <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Téléphone</label>
                        <input type="tel" value={contactForm.telephone} onChange={e => setContactForm({ ...contactForm, telephone: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                      </div>
                      <div>
                        <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Entreprise</label>
                        <input type="text" value={contactForm.entreprise} onChange={e => setContactForm({ ...contactForm, entreprise: e.target.value })} className="lux-input" style={{ width: '100%', marginTop: 6 }} />
                      </div>
                    </div>
                    <div>
                      <label style={{ color: TEXT_MUTED, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Message</label>
                      <textarea rows={4} value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} placeholder="Parlez-nous de votre projet..." className="lux-input" style={{ width: '100%', marginTop: 6, resize: 'vertical', fontFamily: 'inherit' }} />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    className="lux-btn-gold"
                    style={{
                      fontFamily: "'Bodoni Moda', 'Didot', 'Bodoni 72', 'Times New Roman', serif",
                      fontWeight: 700,
                      color: '#ffffff',
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      background: 'linear-gradient(110deg, #4a3208 0%, #6b4810 18%, #8a5e16 38%, #a87420 50%, #8a5e16 62%, #6b4810 82%, #4a3208 100%)',
                      backgroundSize: '200% auto',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)',
                      width: '100%',
                      marginTop: 24,
                      padding: 16,
                      fontSize: 14,
                      letterSpacing: 2.5,
                      textTransform: 'uppercase',
                      opacity: contactSubmitting ? 0.6 : 1,
                      cursor: contactSubmitting ? 'wait' : 'pointer'
                    }}
                  >
                    {contactSubmitting ? 'Envoi...' : 'Envoyer ma demande'}
                  </button>
                </form>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{
                  fontSize: 56,
                  color: GOLD_LIGHT,
                  marginBottom: 18,
                  fontFamily: "'Bodoni Moda', serif",
                  fontWeight: 700
                }}>✓</div>
                <h2 className="lux-serif" style={{
                  fontSize: 'clamp(22px, 3vw, 32px)',
                  fontWeight: 700,
                  margin: '0 0 12px',
                  textTransform: 'uppercase',
                  color: '#ffffff'
                }}>
                  Demande envoyée
                </h2>
                <p style={{
                  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                  fontSize: 13, fontWeight: 400,
                  color: '#d8d2c0', lineHeight: 1.7,
                  margin: '0 0 24px'
                }}>
                  Merci pour votre confiance. Notre équipe vous recontacte sous 24h.
                </p>
                <button
                  onClick={closeContact}
                  className="lux-btn-gold"
                  style={{
                    fontFamily: "'Bodoni Moda', serif",
                    fontWeight: 700,
                    color: '#ffffff',
                    background: 'linear-gradient(110deg, #4a3208 0%, #8a5e16 50%, #4a3208 100%)',
                    backgroundSize: '200% auto',
                    padding: '14px 28px',
                    fontSize: 13,
                    letterSpacing: 2.5,
                    textTransform: 'uppercase'
                  }}
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <footer style={{
        padding: 'clamp(50px, 7vw, 90px) 20px clamp(28px, 3vw, 44px)',
        textAlign: 'center',
        color: TEXT,
        borderTop: `1px solid ${BORDER}`
      }}>
        {/* Titre marque (lien vers le site E-Shops Pro) */}
        <a
          href="https://www.e-shop-pro.fr/"
          target="_blank"
          rel="noopener noreferrer"
          className="lux-serif"
          style={{
            display: 'inline-block',
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: 700,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: '#ffffff',
            marginBottom: 28,
            textDecoration: 'none',
            transition: 'color .2s ease'
          }}
          onMouseEnter={e => { e.currentTarget.style.color = GOLD_LIGHT; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#ffffff'; }}
        >
          E Shops Pro
        </a>

        {/* Liens */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 26,
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: '#ffffff'
        }}>
          {[
            { label: 'Contact', action: 'contact' },
            { label: 'Privacy Policy', href: '/privacy-policy' },
            { label: 'Consent Preferences', href: '/consent-preferences' }
          ].map((link, i, arr) => (
            <span key={link.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
              <a
                href={link.href || '#contact'}
                onClick={link.action === 'contact' ? (e) => { e.preventDefault(); setContactOpen(true); } : undefined}
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  transition: 'color .2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => { e.currentTarget.style.color = GOLD_LIGHT; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#ffffff'; }}
              >
                {link.label}
              </a>
              {i < arr.length - 1 && <span style={{ color: TEXT_MUTED, opacity: 0.6 }}>|</span>}
            </span>
          ))}
        </div>

        {/* Réseaux sociaux */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 22,
          marginBottom: 28
        }}>
          <a
            href="https://www.instagram.com/agenceamstramgram/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            style={{
              color: '#ffffff',
              opacity: 0.85,
              transition: 'opacity .2s ease, color .2s ease',
              display: 'inline-flex'
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = GOLD_LIGHT; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.color = '#ffffff'; }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
          </a>
        </div>

        {/* Copyright */}
        <div style={{
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          fontSize: 11,
          fontWeight: 400,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: TEXT_MUTED,
          opacity: 0.85
        }}>
          All rights reserved — Copyright © {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}

export default BoutiqueInfluenceurs;
